import { forwardRef, ElementType, useMemo } from 'react';
import MuiSkeleton, { SkeletonProps as MuiSkeletonProps } from '@mui/material/Skeleton';
import { styled } from '@mui/material/styles';
import Box from '../Box';
import Stack from '../Stack';
import Typography from '../Typography';

export interface SkeletonProps extends MuiSkeletonProps {
  /**
   * Number of times to repeat the Skeleton component.
   */
  rows?: number;

  /**
   * This prop does literary nothing. It's here just for backward compatibility.
   * @deprecated
   */
  fullWidth?: boolean;

  component?: ElementType<any> | undefined;
}

interface StyledMuiSkeletonProps {
  ownerState: SkeletonProps;
}

const StyledMuiSkeleton = styled(MuiSkeleton)<StyledMuiSkeletonProps>(({ theme, ownerState }) => ({
  maxWidth: '100%',
  color: 'transparent',
  transform: 'none',
  ...(ownerState.children && {
    width: 'fit-content',
  }),
  ...(!ownerState.width &&
    !ownerState.children && {
      width: '100%',
    }),
  '&.MuiSkeleton-text': {
    borderRadius: 4,
    fontSize: theme.typography.body1.fontSize,
    lineHeight: 1,

    '& > *': {
      lineHeight: 1,
      marginTop: 0,
      marginBottom: 0,
    },
  },
}));

const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>((props, ref) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- just removing 'fullWidth' from 'other'
  const { rows, fullWidth: _fullWidth, children, ...other } = props;

  const ownerState = useMemo(
    () => ({
      ...props,
    }),
    [props]
  );

  const copies = useMemo(
    () =>
      rows && rows > 1
        ? new Array(rows).fill(null).map((_, i) => (
            <StyledMuiSkeleton component="div" {...other} key={i} ownerState={ownerState} animation="wave">
              {children}
            </StyledMuiSkeleton>
          ))
        : false,
    [children, other, ownerState, ref, rows]
  );

  return copies ? (
    <Stack direction="column" gap={1} ref={ref} sx={{ maxWidth: '100%' }}>
      {copies.map((c, index) => (
        <Typography key={index}>{c}</Typography>
      ))}
    </Stack>
  ) : (
    <Box ref={ref} sx={{ maxWidth: '100%' }}>
      <StyledMuiSkeleton component="div" {...other} ownerState={ownerState} animation="wave">
        {children}
      </StyledMuiSkeleton>
    </Box>
  );
});

Skeleton.displayName = 'Skeleton';

export default Skeleton;
